import { A, useLocation } from "@solidjs/router";
import { TbLink } from "solid-icons/tb";
import { children, type Component, type JSX, type ParentComponent, Show } from "solid-js";
import { DownloadButton, GetStartedButton } from "~/components/marketing/CTAButtons";
import { copyLink } from "~/components/marketing/SectionHeading";
import { PAGE_SECTIONS } from "~/components/marketing/sections";
import { useI18n } from "~/i18n";
import { hasChiefBuild } from "~/lib/chiefDownload";
import { useOS } from "~/lib/useOS";

// Brand blue is too dark to read as small text on the dark theme's canvas, so text takes its light tint there.
export const BRAND_TEXT = "text-brand dark:text-[#9db3ed]";

/**
 * The mono wayfinding label over a section; `index` numbers the capability
 * sections as one workflow (01 · LISTEN … 03 · HAND OFF). `id` is the section's
 * anchor: the eyebrow then carries the section's link, small beside the label
 * rather than large inside the heading under it.
 */
export const Eyebrow: Component<{ index?: string; label: string; id?: string; class?: string }> = (props) => (
  <p
    class={`font-mono text-xs font-semibold tracking-[0.18em] uppercase ${props.class ?? BRAND_TEXT}`}
    classList={{ "group/heading": !!props.id }}
  >
    <Show when={props.index}>{(index) => `${index()} · `}</Show>
    {props.label}
    <Show when={props.id}>{(id) => <SectionLink id={id()} />}</Show>
  </p>
);

/**
 * A two-line section headline. The catalog entry carries the break: the line
 * after it is the promise, set quieter. A translation that drops the break
 * renders as one line. `linked` puts the section's link in the heading, for a
 * heading with no eyebrow to carry it.
 */
export const SectionTitle: Component<{
  id: string;
  text: string;
  compact?: boolean;
  linked?: boolean;
  class?: string;
}> = (props) => {
  const lines = () => props.text.split("\n");
  return (
    <h2
      id={props.id}
      class={`scroll-mt-28 font-semibold tracking-tight text-balance ${props.compact ? "text-2xl sm:text-3xl" : "text-3xl sm:text-4xl lg:text-5xl"} ${props.class ?? ""}`}
      classList={{ "group/heading": props.linked }}
    >
      {lines()[0]}
      <Show when={props.linked}>
        <SectionLink id={props.id} />
      </Show>
      <Show when={lines()[1]}>{(second) => <span class="block text-secondary-text">{second()}</span>}</Show>
    </h2>
  );
};

/**
 * The link a section heading reveals on hover, for its eyebrow's or heading's `group/heading`; an
 * eyebrow's link also shows while the heading right under it is hovered. It names the section in
 * the query rather than the fragment, which no client sends to the server, so the link unfurls as
 * the section's own card. Only the pages in PAGE_SECTIONS link their sections; the compare, legal
 * and download pages share the headings but not the links. A heading missing from its page's table
 * falls back to its id.
 */
const SectionLink: Component<{ id: string }> = (props) => {
  const { t } = useI18n();
  const location = useLocation();
  const sections = () => PAGE_SECTIONS[location.pathname];
  const href = () => {
    const name = Object.keys(sections() ?? {}).find((key) => sections()?.[key].id === props.id);
    return name ? `${location.pathname}?s=${name}` : `#${props.id}`;
  };
  return (
    <Show when={sections()}>
      <CopyLink href={href()} label={t("marketing.section.copy_link")} />
    </Show>
  );
};

/**
 * A tab's link: the page and the tab's fragment, which the tab system opens on arrival. `/` sends a
 * signed-in visitor to the app unless the query has `landing`, `video` or `s`, so on the home page
 * the link keeps a query that already has one (and with it any audience the hero reads) and adds a
 * bare `?landing` otherwise.
 */
export const tabHref = (pathname: string, search: string, id: string) => {
  if (pathname !== "/") return `${pathname}#${id}`;
  const query = new URLSearchParams(search);
  const pinned = query.has("landing") || query.has("video") || query.has("s");
  return `/${pinned ? search : "?landing"}#${id}`;
};

/**
 * The link a tab reveals on hover of its panel's `group/heading`. Unlike a section's, it is not a
 * `?s=` link, so it unfurls as the page.
 */
export const TabLink: Component<{ id: string }> = (props) => {
  const { t } = useI18n();
  const location = useLocation();
  return <CopyLink href={tabHref(location.pathname, location.search, props.id)} label={t("marketing.tab.copy_link")} />;
};

const CopyLink: Component<{ href: string; label: string }> = (props) => (
  <a
    href={props.href}
    onClick={copyLink}
    aria-label={props.label}
    class="ms-[0.3em] inline-block rounded-sm align-middle text-secondary-text opacity-0 transition-opacity group-hover/heading:opacity-100 hover:text-primary-text focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none [p:has(+h2:hover)>&]:opacity-100"
  >
    <TbLink class="size-[0.6em] in-[p]:size-[1.25em]" aria-hidden="true" />
  </a>
);

/**
 * The hover and focus treatment for a card that is a link: it lifts, and its border and glow take
 * the card's `--c` hue. Pair it with `CardArrow` beside the card's title.
 */
export const LINK_CARD =
  "group transition duration-200 hover:-translate-y-1 hover:border-(--c)/40 hover:shadow-[0_16px_40px_-20px_var(--c)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand motion-reduce:transition-none motion-reduce:hover:translate-y-0";

export const CardArrow = () => (
  <span
    aria-hidden="true"
    class="inline-block transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
  >
    →
  </span>
);

export const TextLink: Component<{ href: string; label: string; class?: string; describedBy?: string }> = (props) => (
  <A
    href={props.href}
    aria-describedby={props.describedBy}
    class={`group inline-flex items-center gap-1.5 rounded-sm text-sm font-semibold hover:underline focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none ${props.class ?? BRAND_TEXT}`}
  >
    {props.label}
    <span aria-hidden="true" class="transition-transform group-hover:translate-x-0.5">
      →
    </span>
  </A>
);

/** The soft, dotted stage a mockup sits on. */
export const MockPanel: ParentComponent<{ class?: string }> = (props) => (
  <div
    class={`relative rounded-3xl border border-primary-border bg-panel bg-[radial-gradient(var(--color-primary-border)_1px,transparent_1px)] bg-size-[20px_20px] p-3 sm:p-6 lg:p-8 ${props.class ?? ""}`}
  >
    {props.children}
  </div>
);

/**
 * A capability section: copy on one side, the product doing it on the other,
 * alternating sides down the page and stacking on mobile. Children render under
 * the body, above the link.
 */
export const SplitRow: ParentComponent<{
  id: string;
  index: string;
  eyebrow: string;
  title: string;
  body: string;
  link?: { href: string; label: string };
  visual: JSX.Element;
  reverse?: boolean;
}> = (props) => (
  <section aria-labelledby={props.id} class="px-4 py-20 sm:px-6 sm:py-28 lg:px-8">
    <div
      class="mx-auto grid max-w-7xl items-center gap-10 lg:gap-16"
      classList={{
        "lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]": !props.reverse,
        "lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]": props.reverse,
      }}
    >
      <div class="flex flex-col gap-5" classList={{ "lg:order-2": props.reverse }}>
        <Eyebrow index={props.index} label={props.eyebrow} id={props.id} />
        <SectionTitle id={props.id} text={props.title} />
        <p class="max-w-xl text-lg leading-relaxed text-secondary-text">{props.body}</p>
        {props.children}
        <Show when={props.link}>
          {(link) => <TextLink href={link().href} label={link().label} describedBy={props.id} />}
        </Show>
      </div>
      <MockPanel class={props.reverse ? "lg:order-1" : undefined}>{props.visual}</MockPanel>
    </div>
  </section>
);

/**
 * The top of a marketing page: the page's one h1, a sub-line, Download + Get
 * started, and the visual under them. The title's line break sets the second
 * line in brand blue. `from` tags the download click.
 */
export const PageHero: ParentComponent<{
  eyebrow: string;
  title: string;
  sub: string;
  from: string;
  class?: string;
}> = (props) => {
  const os = useOS();
  const lines = () => props.title.split("\n");
  const visual = children(() => props.children);
  return (
    <section class={`relative overflow-hidden px-4 pt-14 pb-16 sm:px-6 sm:pt-20 sm:pb-24 lg:px-8 ${props.class ?? ""}`}>
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 bg-[radial-gradient(var(--color-primary-border)_1px,transparent_1px)] mask-[radial-gradient(ellipse_at_top,black_30%,transparent_75%)] bg-size-[24px_24px]"
      />
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -top-40 left-1/2 h-112 w-240 -translate-x-1/2 rounded-full bg-brand/10 blur-3xl"
      />
      <div class="relative mx-auto flex max-w-4xl flex-col items-center text-center">
        <Eyebrow label={props.eyebrow} />
        <h1 class="mt-5 text-4xl font-semibold tracking-tight text-balance sm:text-6xl">
          {lines()[0]}
          <Show when={lines()[1]}>
            {(second) => <span class="block text-brand dark:text-brand-hover">{second()}</span>}
          </Show>
        </h1>
        <p class="mt-6 max-w-2xl text-lg leading-relaxed text-balance text-secondary-text sm:text-xl">{props.sub}</p>
        <div class="mt-9 flex flex-wrap items-center justify-center gap-3">
          <DownloadButton from={props.from} size="lg" />
          <Show when={hasChiefBuild(os())}>
            <GetStartedButton variant="secondary" size="lg" />
          </Show>
        </div>
      </div>
      <Show when={visual()}>
        <div class="relative mx-auto mt-14 max-w-6xl sm:mt-20">{visual()}</div>
      </Show>
    </section>
  );
};
